import { useContext, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { Context } from "../../../context/Context";
import { Button, X } from "../../ui";
import { useModalLayer } from "../../modals/modalHooks";
import "./CampaignActions.css";

// Dialog shell shared by the Campaign Actions flows. It renders in a portal on
// document.body, so a card's transform/overflow/z-index (cards lift on hover)
// can never turn the overlay into a card-sized box: it always covers the
// viewport and is centred on it.
export default function CampaignModal({ title, icon, onClose, busy = false, children, footer, closeLabel }) {
  const { lang } = useContext(Context);
  const dialogRef = useRef(null);
  const pressedBackdropRef = useRef(false);
  const titleId = useId();
  const requestClose = () => {
    if (!busy) onClose();
  };
  useModalLayer(true, { containerRef: dialogRef, onEscape: requestClose });

  // Close only when the press and the release both happen on the backdrop, so
  // selecting text inside the dialog and releasing outside never closes it.
  const overlayProps = {
    onMouseDown: (event) => {
      pressedBackdropRef.current = event.target === event.currentTarget;
    },
    onClick: (event) => {
      if (pressedBackdropRef.current && event.target === event.currentTarget) requestClose();
      pressedBackdropRef.current = false;
    },
  };

  return createPortal(
    <div className="ca-overlay" role="presentation" dir={lang === "ar" ? "rtl" : "ltr"} {...overlayProps}>
      <div
        ref={dialogRef}
        className="ca-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-busy={busy || undefined}
        tabIndex={-1}
      >
        <header className="ca-modal-head">
          <h4 id={titleId}>
            {icon}
            <span>{title}</span>
          </h4>
          <Button
            appearance="ghost"
            size="sm"
            rounded
            icon={<X size={18} />}
            aria-label={closeLabel}
            title={closeLabel}
            disabled={busy}
            onClick={requestClose}
          />
        </header>
        <div className="ca-modal-body">{children}</div>
        {footer && <footer className="ca-modal-foot">{footer}</footer>}
      </div>
    </div>,
    document.body,
  );
}
